// React
import { useState, useContext, useMemo } from "react";
import { useQuery, gql, useMutation } from "@apollo/client";

// Components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import Footer from "components/Footer";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import SoftButton from "components/SoftButton";
import ModalDelete from "components/Modals/Delete";
import { Card, Grid, Icon, Tooltip, Menu, MenuItem, Select, Collapse } from "@mui/material";

// Data
import dataUsuarios from "layouts/convenios/data/dataUsuarios";
import Loading from "components/Carteles/Loading";
import DataTable from "components/Tables/DataTable";
import SinDatos from "components/Carteles/SinDatos";
import CustomPagination from "components/Pagination";
import { getUproColor } from "utils/colors";
import SoftInput from "components/SoftInput";
import ModalDocumentos from "./ModalDocumentos";
import { MessageManager } from "context";
import { UserContext } from "context/user";
import { ConfirmarContext } from "context/ConfirmarContext";
import ModalConvenio from "./ModalConvenio";
import ModalAddSolicitud from "./ModalAddSolicitud";

function Convenios() {
  const [search, setSearch] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [openMenu, setOpenMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openModalDocumentos, setOpenModalDocumentos] = useState(false);
  const [openModalSolcitud, setOpenModalSolicitud] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({});
  const [openFilters, setOpenFilters] = useState(false);
  const [pagesInfo, setPagesInfo] = useState({
    pages: 0,
    total: 0,
  });
  const [orderBy, setOrderBy] = useState({
    campo: "id",
    orden: "DESC",
  });
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const { confirmar } = useContext(ConfirmarContext);

  const { handleSnackbar } = useContext(MessageManager);
  const { columns, getRows } = dataUsuarios;

  const [saveUsuario, { loading: loadingSave }] = useMutation(
    gql`
      mutation saveUser($input: UserInput!) {
        saveUser(input: $input) {
          id
        }
      }
    `
  );

  const [rechazarUserConvenio] = useMutation(
    gql`
      mutation rechazarUserConvenio($id: ID!, $motivo: String!) {
        rechazarUserConvenio(id: $id, motivo: $motivo) {
          id
        }
      }
    `
  );

  const { data: dataConvenios } = useQuery(
    gql`
      query convenios {
        convenios {
          id
          nombreInstitucion
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  const handleOrderBy = (campo) => {
    if (orderBy.campo === campo) {
      if (orderBy.orden === "ASC") {
        setOrderBy({
          campo: campo,
          orden: "DESC",
        });
      } else {
        setOrderBy({
          campo: campo,
          orden: "ASC",
        });
      }
    } else {
      setOrderBy({
        campo: campo,
        orden: "ASC",
      });
    }
  };

  const handleOpenMenu = (event, item) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(true);
    setSelectedUsuario(item);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setOpenMenu(false);
  };

  const handleSearch = (event) => {
    const newTimeout = setTimeout(() => {
      setSearch(event.target.value);
      setPage(1);
    }, 500);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(newTimeout);
  };

  const [deleteUser] = useMutation(
    gql`
      mutation deleteUser($id: ID!) {
        deleteUser(id: $id) {
          id
        }
      }
    `
  );

  const { loading, data, refetch } = useQuery(
    gql`
      query getUsuarios($filter: JSON, $search: String, $page: Int, $order: Order) {
        paginationInfo {
          pages
          total
        }
        userConvenios(
          search: $search
          pagination: { page: $page, limit: 10 }
          order: $order
          filter: $filter
        ) {
          id
          nombre
          apellido
          dni
          email
          tipoDocumento
          estado
          motivoRechazo
          telefono
          convenio {
            id
            nombreInstitucion
          }
          localidad {
            id
            nombre
            provincia {
              id
              nombre
            }
          }
          documentosFile
          certificadoFile
          tipoUsuario
        }
      }
    `,
    {
      variables: {
        search: search || null,
        page: page,
        order: { field: orderBy.campo, order: orderBy.orden },
        filter: filter,
      },
      fetchPolicy: "network-only",
    }
  );

  const handleFilter = (e) => {
    const { name, value } = e.target;
    setFilter((prev) => ({
      ...prev,
      [name]: value === "0" ? undefined : value,
    }));
  };
  const renderMenu = (
    <Menu id="simple-menu" anchorEl={anchorEl} open={Boolean(openMenu)} onClose={handleCloseMenu}>
      <MenuItem
        disabled={false}
        onClick={() => {
          setOpenModalDocumentos(true);
          handleCloseMenu();
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: "100%",
          }}
        >
          <SoftBox>
            <SoftTypography variant="p">Ver documentos</SoftTypography>
          </SoftBox>
          <SoftBox>
            <Icon
              fontSize="small"
              sx={{
                verticalAlign: "middle",
                ml: 1,
              }}
            >
              visibility
            </Icon>
          </SoftBox>
        </SoftBox>
      </MenuItem>
      {selectedUsuario?.estado == "PENDIENTE" && (
        <MenuItem
          disabled={selectedUsuario?.tipoUser === "Administrador"}
          onClick={() => {
            confirmar({
              title: "Crear un usuario",
              message: "¿Está seguro que desea crear un usuario con acceso al sistema?",
              icon: "warning",
              func: () => {
                saveUsuario({
                  variables: {
                    input: {
                      nombre: selectedUsuario?.nombre,
                      apellido: selectedUsuario?.apellido,
                      dni: selectedUsuario?.dni,
                      email: selectedUsuario?.email,
                      username: selectedUsuario?.dni,
                      tipoUser: "Convenio",
                      idUserConvenio: selectedUsuario?.id,
                      telefono: selectedUsuario?.telefono || null,
                    },
                  },
                })
                  .then(() => {
                    refetch();
                    handleSnackbar("Usuario guardado correctamente", "success");
                  })
                  .catch((err) => {
                    handleSnackbar("Error al guardar el usuario: " + err, "error");
                  });
                handleCloseMenu();
              },
            });
          }}
        >
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              width: "100%",
            }}
          >
            <SoftBox>
              <SoftTypography variant="p">Aprobar</SoftTypography>
            </SoftBox>
            <SoftBox>
              <Icon
                fontSize="small"
                sx={{
                  verticalAlign: "middle",
                  color:
                    selectedUsuario?.tipoUser === "Administrador"
                      ? getUproColor()
                      : getUproColor("uproGreen"),
                }}
              >
                check
              </Icon>
            </SoftBox>
          </SoftBox>
        </MenuItem>
      )}
      {selectedUsuario?.estado == "PENDIENTE" && (
        <MenuItem
          onClick={() => {
            confirmar({
              title: "Rechazar solicitud",
              message: "¿Está seguro que desea rechazar la solicitud?",
              icon: "warning",
              pedirMotivo: true,
              func: (motivo) => {
                rechazarUserConvenio({
                  variables: {
                    id: selectedUsuario?.id,
                    motivo: motivo,
                  },
                })
                  .then(() => {
                    refetch();
                    handleSnackbar("Solicitud rechazada correctamente", "success");
                  })
                  .catch((err) => {
                    handleSnackbar("Error al rechazar la solicitud: " + err, "error");
                  });
                handleCloseMenu();
              },
            });
          }}
        >
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              width: "100%",
            }}
          >
            <SoftBox>
              <SoftTypography variant="p">Rechazar</SoftTypography>
            </SoftBox>
            <SoftBox>
              <Icon
                fontSize="small"
                sx={{
                  verticalAlign: "middle",
                  color: getUproColor("uproPink"),
                }}
              >
                close
              </Icon>
            </SoftBox>
          </SoftBox>
        </MenuItem>
      )}
    </Menu>
  );

  const handleDeleteUser = async (id) => {
    try {
      await deleteUser({ variables: { id } });
      refetch();
      handleSnackbar("Usuario eliminado correctamente", "success");
    } catch (error) {
      handleSnackbar("Error al eliminar usuario", "error");
    }
  };

  const allRows = useMemo(() => {
    if (data) {
      setPagesInfo(data.paginationInfo);
      return getRows(data.userConvenios, handleOpenMenu);
    }
  }, [data]);

  const [openModalConvenio, setOpenModalConvenio] = useState(false);
  const handleOpenConvenio = () => {
    setOpenModalConvenio(true);
  };
  const handleCloseConvenio = () => {
    setOpenModalConvenio(false);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Card>
            <Grid container spacing={2} p={3}>
              <Grid item xs={12}>
                <SoftBox
                  display={{
                    xs: "flex-row",
                    sm: "flex",
                  }}
                  justifyContent="space-between"
                  alignItems="center"
                  gap={1}
                >
                  <SoftBox
                    flex={1}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <SoftTypography variant="h6">Solicitudes</SoftTypography>
                    <Tooltip title="Filtrar">
                      <SoftButton
                        color="dark"
                        circular
                        iconOnly
                        onClick={() => setOpenFilters(!openFilters)}
                      >
                        <Icon>{openFilters ? "filter_list_off" : "filter_list"}</Icon>
                      </SoftButton>
                    </Tooltip>
                  </SoftBox>
                  <SoftBox
                    display="flex"
                    alignItems="center"
                    justifyContent="end"
                    flexDirection={{ xs: "column-reverse", sm: "row" }}
                    gap={1}
                    mt={{
                      xs: 2,
                      sm: 0,
                    }}
                  >
                    <SoftBox
                      sx={{
                        width: {
                          xs: "100%",
                          sm: "auto",
                        },
                      }}
                    >
                      <SoftInput
                        placeholder="Escribe aquí..."
                        icon={{ component: "search", direction: "left" }}
                        onChange={handleSearch}
                      />
                    </SoftBox>
                    <SoftBox width={{ xs: "100%", sm: "auto" }}>
                      <Tooltip title="Convenios" placement="top">
                        <SoftButton
                          color="uproGreen"
                          circular
                          fullWidth
                          onClick={() => handleOpenConvenio(true)}
                        >
                          <Icon>groups</Icon>
                          &nbsp;convenios
                        </SoftButton>
                      </Tooltip>
                    </SoftBox>
                    <SoftBox width={{ xs: "100%", sm: "auto" }}>
                      <Tooltip title="Agregar solicitud" placement="top">
                        <SoftButton
                          color="uproPink"
                          circular
                          fullWidth
                          onClick={() => setOpenModalSolicitud(true)}
                        >
                          <Icon>add</Icon>
                          &nbsp;Agregar
                        </SoftButton>
                      </Tooltip>
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
                <Collapse in={openFilters}>
                  <SoftBox
                    display="flex"
                    justifyContent="flex-end"
                    mt={1}
                    gap={1}
                    flexDirection={{ xs: "column", sm: "row" }}
                  >
                    <SoftBox width={{ xs: "100%", sm: "auto" }}>
                      <Select
                        name="idConvenio"
                        onChange={handleFilter}
                        value={filter.idConvenio || "0"}
                      >
                        <MenuItem value="0">
                          <SoftBox display="flex" alignItems="center" gap={1}>
                            <Icon fontSize="small" color="secondary">
                              groups
                            </Icon>
                            Todos los convenios
                          </SoftBox>
                        </MenuItem>
                        {dataConvenios?.convenios?.map((convenio) => (
                          <MenuItem key={convenio.id} value={convenio.id}>
                            <SoftBox display="flex" alignItems="center" gap={1}>
                              <Icon fontSize="small" sx={{ color: getUproColor("uproGreen") }}>
                                groups
                              </Icon>
                              {convenio.nombreInstitucion}
                            </SoftBox>
                          </MenuItem>
                        ))}
                      </Select>
                    </SoftBox>

                    <SoftBox width={{ xs: "100%", sm: "auto" }}>
                      <Select name="estado" onChange={handleFilter} value={filter.estado || "0"}>
                        <MenuItem value="0">
                          <SoftBox display="flex" alignItems="center" gap={1}>
                            <Icon fontSize="small" color="secondary">
                              bookmarks
                            </Icon>
                            Todos los estados
                          </SoftBox>
                        </MenuItem>
                        <MenuItem value="pendiente">
                          <SoftBox display="flex" alignItems="center" gap={1}>
                            <Icon fontSize="small" sx={{ color: getUproColor("uproYellow") }}>
                              access_time
                            </Icon>
                            Pendiente
                          </SoftBox>
                        </MenuItem>
                        <MenuItem value="aprobado">
                          <SoftBox display="flex" alignItems="center" gap={1}>
                            <Icon fontSize="small" sx={{ color: getUproColor("uproGreen") }}>
                              check
                            </Icon>
                            Aprobado
                          </SoftBox>
                        </MenuItem>
                        <MenuItem value="rechazado">
                          <SoftBox display="flex" alignItems="center" gap={1}>
                            <Icon fontSize="small" sx={{ color: getUproColor("uproPink") }}>
                              close
                            </Icon>
                            Rechazado
                          </SoftBox>
                        </MenuItem>
                      </Select>
                    </SoftBox>
                  </SoftBox>
                </Collapse>
              </Grid>
            </Grid>
            <SoftBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <Loading />
              ) : allRows?.length > 0 ? (
                <DataTable
                  columns={columns}
                  rows={allRows}
                  order={orderBy}
                  setOrder={handleOrderBy}
                />
              ) : (
                <SinDatos />
              )}
            </SoftBox>
            <CustomPagination
              loading={loading}
              length={data?.userConvenios?.length}
              total={pagesInfo?.total}
              pages={pagesInfo?.pages}
              page={page}
              setPage={setPage}
              noCard
            />
          </Card>
          {renderMenu}
          <ModalDocumentos
            open={openModalDocumentos}
            handleClose={() => {
              setOpenModalDocumentos(false);
              setSelectedUsuario();
            }}
            refetch={refetch}
            selectedUsuario={selectedUsuario}
          />
          <ModalDelete
            open={openModalDelete}
            handleClose={() => setOpenModalDelete(false)}
            nombre={`el usuario ${selectedUsuario?.nombre} ${selectedUsuario?.apellido}`}
            functionDelete={() => {
              handleDeleteUser(selectedUsuario.id);
              setOpenModalDelete(false);
            }}
          />
          <ModalConvenio open={openModalConvenio} handleClose={handleCloseConvenio} />
          <ModalAddSolicitud
            open={openModalSolcitud}
            handleClose={() => setOpenModalSolicitud(false)}
            refetchParent={refetch}
          />
        </SoftBox>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Convenios;
